import React, { useState } from 'react';
import { XiaohuAINewsCrawler, XiaohuAINews } from '../services/xiaohuAINewsCrawler';

const XiaohuAINewsTest: React.FC = () => {
  const [news, setNews] = useState<XiaohuAINews[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [serviceStatus, setServiceStatus] = useState<boolean | null>(null);

  const testNewsCrawler = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const crawler = new XiaohuAINewsCrawler();
      
      // 检查服务可用性
      const isAvailable = await crawler.checkServiceAvailability();
      setServiceStatus(isAvailable);
      
      // 获取新闻
      const newsData = await crawler.fetchLatestNews(10);
      setNews(newsData);
      
      if (newsData.length === 0) {
        setError('未获取到新闻数据，可能是API限制或网站结构变化');
      }
    } catch (err) {
      setError(`获取小虎AI新闻失败: ${err instanceof Error ? err.message : '未知错误'}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="bg-white rounded-lg shadow-lg p-6">
        <h2 className="text-2xl font-bold mb-4 text-gray-800">🕷️ 小虎AI新闻爬虫测试</h2>
        
        <div className="mb-6">
          <button
            onClick={testNewsCrawler}
            disabled={loading}
            className="bg-blue-500 hover:bg-blue-600 disabled:bg-gray-400 text-white px-6 py-2 rounded-lg transition-colors"
          >
            {loading ? '🔄 正在获取新闻...' : '🚀 测试小虎AI新闻爬虫'}
          </button>
        </div>

        {/* 服务状态 */}
        <div className="mb-4">
          <h3 className="text-lg font-semibold mb-2">服务状态</h3>
          <div className="flex items-center space-x-2">
            <span className={`w-3 h-3 rounded-full ${
              serviceStatus === null ? 'bg-gray-400' : 
              serviceStatus ? 'bg-green-400' : 'bg-red-400'
            }`}></span>
            <span className="text-sm">
              {serviceStatus === null ? '未检测' : 
               serviceStatus ? '✅ 小虎AI网站可访问' : '❌ 小虎AI网站无法访问'}
            </span>
          </div>
        </div>

        {/* 错误信息 */}
        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
            <h3 className="text-lg font-semibold text-red-800 mb-2">错误信息</h3>
            <p className="text-red-600">{error}</p>
          </div>
        )}

        {/* 新闻列表 */}
        {news.length > 0 && (
          <div>
            <h3 className="text-lg font-semibold mb-4">📰 获取的新闻 ({news.length}条)</h3>
            <div className="space-y-4">
              {news.map((item, index) => (
                <div key={index} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                  <div className="flex justify-between items-start mb-2">
                    <h4 className="text-md font-medium text-gray-900 flex-1 mr-4">
                      {item.url ? (
                        <a 
                          href={item.url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="hover:text-blue-600 hover:underline"
                        >
                          {item.title}
                        </a>
                      ) : (
                        item.title
                      )}
                    </h4>
                    <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full whitespace-nowrap">
                      {item.category}
                    </span>
                  </div>
                  
                  {item.summary && (
                    <p className="text-sm text-gray-600 mb-2">{item.summary}</p>
                  )}
                  
                  <div className="flex justify-between items-center text-xs text-gray-500">
                    <span>📅 {new Date(item.publishedAt).toLocaleString('zh-CN')}</span>
                    <span>🏷️ {item.source}</span>
                  </div>
                  
                  {item.tags && item.tags.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1">
                      {item.tags.map((tag, tagIndex) => (
                        <span 
                          key={tagIndex}
                          className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded"
                        >
                          #{tag}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {loading && (
          <div className="text-center py-8">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
            <p className="mt-2 text-gray-600">正在爬取小虎AI新闻...</p>
          </div>
        )}

        {!loading && news.length === 0 && !error && (
          <div className="text-center py-8 text-gray-500">
            点击上方按钮开始测试小虎AI新闻爬虫
          </div>
        )}

        {/* 技术说明 */}
        <div className="mt-8 p-4 bg-gray-50 rounded-lg">
          <h3 className="text-lg font-semibold mb-2">🔧 技术实现说明</h3>
          <ul className="text-sm text-gray-600 space-y-1">
            <li>• <strong>多层次爬取策略</strong>：API接口 → 页面爬取 → 备用内容</li>
            <li>• <strong>智能内容解析</strong>：支持JSON、RSS、HTML多种格式</li>
            <li>• <strong>反爬虫应对</strong>：模拟真实浏览器请求头，避免被检测</li>
            <li>• <strong>容错机制</strong>：即使网站无法访问也提供备用新闻内容</li>
            <li>• <strong>实时性保证</strong>：获取最新发布的AI新闻和前沿动态</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default XiaohuAINewsTest; 